import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, X, CurrencyEur, Pencil } from "@phosphor-icons/react";

/**
 * ScanDialog — workflow when a barcode is scanned (or note menu ΕΚΤΕΛΕΣΤΗΚΕ clicked).
 *
 * States:
 *  1. note not in_progress → "ΠΟΙΟΣ ΑΝΑΛΑΜΒΑΝΕΙ;" choose from PEOPLE
 *  2. note in_progress + mode=full → "ΟΛΟΚΛΗΡΩΘΗΚΕ ή ΔΙΟΡΘΩΣΗ;"
 *  3. complete-confirm → "ΜΕ €; ΝΑΙ / ΟΧΙ"
 *
 * mode prop:
 *  - "scan"      : full workflow (default for barcode scan)
 *  - "complete"  : skip step 1+2, go directly to € confirm (used when in_progress + ΕΚΤΕΛΕΣΤΗΚΕ pressed)
 */
export default function ScanDialog({
  open,
  onOpenChange,
  note,
  people,
  mode = "scan",
  onAssign,        // (note, person) => void
  onComplete,      // (note, needsPayment) => void
  onCorrect,       // (note) => void  — opens edit dialog
}) {
  const [step, setStep] = useState("init");

  useEffect(() => {
    if (!open || !note) return;
    if (mode === "complete") {
      // Skip assign / decide step
      setStep("ask-payment");
    } else if (!note.in_progress || !note.in_progress_by) {
      setStep("assign");
    } else {
      setStep("decide");
    }
  }, [open, note, mode]);

  if (!note) return null;

  const customer = note?.data?.customer || "ΧΩΡΙΣ ΠΕΛΑΤΗ";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[440px] rounded-2xl p-0 overflow-hidden border-none"
        data-testid="scan-dialog"
      >
        <div className="px-7 pt-6 pb-3" style={{ backgroundColor: note.color || "#fef9c3" }}>
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold text-ink" data-testid="scan-title">
              {customer}
            </DialogTitle>
            <DialogDescription className="text-ink/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              {mode === "complete" ? "ΟΛΟΚΛΗΡΩΣΗ ΕΡΓΑΣΙΑΣ" : "ΣΑΡΩΣΗ POST"}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 pb-6 pt-5 bg-white space-y-4">
          {step === "assign" && (
            <div data-testid="scan-step-assign">
              <p className="text-sm font-bold uppercase tracking-wider text-ink/75 mb-3">
                ΠΟΙΟΣ ΑΝΑΛΑΜΒΑΝΕΙ;
              </p>
              <div className="grid grid-cols-2 gap-2">
                {people.map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      onAssign(note, p);
                      onOpenChange(false);
                    }}
                    className="px-4 py-3 rounded-xl bg-ink/5 hover:bg-ink/15 transition-colors font-bold uppercase tracking-wider text-sm text-ink"
                    data-testid={`scan-assign-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === "decide" && (
            <div data-testid="scan-step-decide">
              <p className="text-sm font-bold uppercase tracking-wider text-ink/75 mb-1">
                ΣΕ ΕΞΕΛΙΞΗ ΑΠΟ:{" "}
                <span className="text-red-700">{note.in_progress_by}</span>
              </p>
              <p className="text-xs text-ink/60 mb-4">ΤΙ ΘΕΛΕΙΣ ΝΑ ΚΑΝΕΙΣ;</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setStep("ask-payment")}
                  className="px-4 py-3 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                  data-testid="scan-decide-complete"
                >
                  <Check size={16} weight="bold" />
                  ΟΛΟΚΛΗΡΩΘΗΚΕ
                </button>
                <button
                  onClick={() => {
                    onCorrect(note);
                    onOpenChange(false);
                  }}
                  className="px-4 py-3 rounded-xl bg-ink/5 hover:bg-ink/15 text-ink font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                  data-testid="scan-decide-correct"
                >
                  <Pencil size={16} weight="bold" />
                  ΔΙΟΡΘΩΣΗ
                </button>
              </div>
            </div>
          )}

          {step === "ask-payment" && (
            <div data-testid="scan-step-payment">
              <p className="text-sm font-bold uppercase tracking-wider text-ink/75 mb-1">
                ΕΡΓΑΣΙΑ ΑΠΟ:{" "}
                <span className="text-ink">{note.in_progress_by || "—"}</span>
              </p>
              <p className="text-xs text-ink/60 mb-4">
                ΝΑ ΜΑΡΚΑΡΙΣΤΕΙ ΜΕ € (ΕΚΚΡΕΜΕΙ ΧΡΕΩΣΗ);
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    onComplete(note, true);
                    onOpenChange(false);
                  }}
                  className="px-4 py-3 rounded-xl bg-amber-400 hover:bg-amber-500 text-ink font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                  data-testid="scan-payment-yes"
                >
                  <CurrencyEur size={18} weight="bold" />
                  ΝΑΙ — ΕΚΚΡΕΜΕΙ
                </button>
                <button
                  onClick={() => {
                    onComplete(note, false);
                    onOpenChange(false);
                  }}
                  className="px-4 py-3 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2"
                  data-testid="scan-payment-no"
                >
                  <X size={16} weight="bold" />
                  ΟΧΙ
                </button>
              </div>
            </div>
          )}

          <div className="text-[10px] uppercase tracking-widest text-ink/40 font-bold pt-2 border-t border-black/10">
            POST: {note.seq || note.id}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
